import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { GlobeAltIcon } from "@heroicons/react/24/outline";
import NavbarFooter from "../components/Footer";
import useTranslation, { AllowedLanguages } from "../hooks/useTranslation";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { StartLearningOnboardingFlow } from "./start-learning";

export const Route = createFileRoute("/")({
  validateSearch: (search: Record<string, unknown>) => ({
    onboarding: search.onboarding === "1" ? "1" : undefined,
  }),
  component: HomePage,
});

function HomePage() {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const onboardingOpen = search.onboarding === "1";

  const [localStorageLanguage, setLocalStorageLanguage] =
    useLocalStorage<AllowedLanguages>("local-language", "ENGLISH");

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLocalStorageLanguage(e.target.value as AllowedLanguages);
  };

  return (
    <>
      {onboardingOpen && (
        <div className="fixed inset-0 z-[70] flex items-stretch justify-center bg-black/35 p-0 sm:items-center sm:p-5">
          <div className="h-full w-full max-w-none sm:h-auto sm:max-w-4xl">
            <StartLearningOnboardingFlow
              embedded
              onClose={() =>
                navigate({ to: "/", search: { onboarding: undefined } })
              }
            />
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[420px_minmax(0,1fr)]">
        <aside className="sticky top-0 h-screen overflow-y-auto bg-white">
          <div className="flex h-full flex-col gap-8 p-8 lg:p-12">
            <div className="flex items-center gap-2">
              <GlobeAltIcon className="h-4 w-4 text-neutral-400" />
              <select
                className="rounded-full border border-neutral-300 bg-white px-3 py-2 text-base text-neutral-800 focus:outline-hidden"
                value={localStorageLanguage}
                onChange={handleLanguageChange}
              >
                <option value="ENGLISH">En</option>
                <option value="GERMAN">De</option>
              </select>
            </div>

            <div className="flex-1 space-y-6">
              <p className="text-xs font-medium uppercase tracking-[0.15em] text-neutral-400">
                {translate("landingpage_archive_brand")}
              </p>

              <h1 className="text-3xl font-semibold leading-[1.08] tracking-[-0.02em] text-neutral-900 sm:text-4xl lg:text-5xl">
                {translate("landingpage_archive_title")}
              </h1>

              <div className="space-y-4">
                <p className="text-base leading-7 text-neutral-600">
                  {translate("landingpage_archive_body_1")}
                </p>
                <p className="text-base leading-7 text-neutral-600">
                  {translate("landingpage_archive_body_2")}
                </p>
                <p className="text-base leading-7 text-neutral-600">
                  {translate("landingpage_archive_body_3")}
                </p>
              </div>

              <p className="text-base font-medium text-neutral-900">
                {translate("landingpage_archive_cta_start")} &rarr;
              </p>
            </div>

            <nav className="flex flex-wrap gap-x-6 gap-y-2">
              <Link
                to="/pricing"
                className="text-xs text-neutral-400 transition-colors hover:text-neutral-700"
              >
                {translate("website_nav_pricing")}
              </Link>
              <Link
                to="/about"
                className="text-xs text-neutral-400 transition-colors hover:text-neutral-700"
              >
                {translate("website_nav_about")}
              </Link>
              <Link
                to="/app/reeprise"
                search={{ mobileView: undefined }}
                className="text-xs text-neutral-400 transition-colors hover:text-neutral-700"
              >
                {translate("website_nav_to_app")}
              </Link>
            </nav>
          </div>
        </aside>

        <main className="bg-white pb-24 pt-12 lg:pt-16">
          <div className="grid grid-cols-1 gap-4 px-4 sm:grid-cols-2 sm:px-6 lg:px-8">
            <ArchiveCardAdaptation />
            <ArchiveCardProgression />
            <ArchiveCardContent />
            <ArchiveCardLexicon />
            <ArchiveCardVocabulary />
            <ArchiveCardCreation />
            <ArchiveCardInsight />
            <ArchiveCardClosing />
          </div>
        </main>
      </div>

      <NavbarFooter />
    </>
  );
}

function ArchiveCard({
  kickerKey,
  titleKey,
  bodyKey,
  span = "col-span-1",
  children,
}: {
  kickerKey: string;
  titleKey: string;
  bodyKey: string;
  span?: "col-span-1" | "col-span-2";
  children?: React.ReactNode;
}) {
  const { translate } = useTranslation();

  return (
    <article
      className={`${span} rounded-xl bg-gray-50 p-6 sm:p-8`}
    >
      <p className="text-[0.7rem] uppercase tracking-[0.22em] text-neutral-400">
        {translate(kickerKey)}
      </p>
      <h2 className="mt-4 text-xl font-semibold leading-[1.15] tracking-[-0.02em] text-neutral-900 sm:text-2xl lg:text-3xl">
        {translate(titleKey)}
      </h2>
      <p className="mt-4 text-sm leading-7 text-neutral-600 sm:text-base">
        {translate(bodyKey)}
      </p>
      {children}
    </article>
  );
}

function ArchiveCardAdaptation() {
  const { translate } = useTranslation();

  return (
    <ArchiveCard
      kickerKey="landingpage_archive_card_adaptation_kicker"
      titleKey="landingpage_archive_card_adaptation_title"
      bodyKey="landingpage_archive_card_adaptation_body"
      span="col-span-2"
    >
      <div className="mt-6 grid gap-6 sm:grid-cols-2">
        <div>
          <p className="text-[0.65rem] uppercase tracking-[0.18em] text-neutral-400">
            Original
          </p>
          <p className="mt-2 text-sm leading-7 text-neutral-700 sm:text-base">
            {translate("landingpage_archive_card_adaptation_original")}
          </p>
        </div>
        <div>
          <p className="text-[0.65rem] uppercase tracking-[0.18em] text-neutral-400">
            {translate("landingpage_archive_card_adaptation_adapted_label")}
          </p>
          <p className="mt-2 text-sm leading-7 text-neutral-700 sm:text-base">
            {translate("landingpage_archive_card_adaptation_adapted")}
          </p>
        </div>
      </div>
    </ArchiveCard>
  );
}

function ArchiveCardProgression() {
  const { translate } = useTranslation();

  return (
    <ArchiveCard
      kickerKey="landingpage_archive_card_progression_kicker"
      titleKey="landingpage_archive_card_progression_title"
      bodyKey="landingpage_archive_card_progression_body"
      span="col-span-1"
    >
      <div className="mt-6 space-y-5">
        <div>
          <p className="text-[0.6rem] uppercase tracking-[0.16em] text-neutral-400">
            {translate("landingpage_archive_card_progression_beginner")}
          </p>
          <p className="mt-1 text-xs leading-6 text-neutral-600">
            {translate("landingpage_archive_card_progression_beginner_text")}
          </p>
        </div>
        <div>
          <p className="text-[0.6rem] uppercase tracking-[0.16em] text-neutral-400">
            {translate("landingpage_archive_card_progression_intermediate")}
          </p>
          <p className="mt-1 text-xs leading-6 text-neutral-600">
            {
              translate(
                "landingpage_archive_card_progression_intermediate_text",
              )
            }
          </p>
        </div>
        <div>
          <p className="text-[0.6rem] uppercase tracking-[0.16em] text-neutral-400">
            {translate("landingpage_archive_card_progression_advanced")}
          </p>
          <p className="mt-1 text-xs leading-6 text-neutral-600">
            {translate("landingpage_archive_card_progression_advanced_text")}
          </p>
        </div>
      </div>
    </ArchiveCard>
  );
}

function ArchiveCardContent() {
  const { translate } = useTranslation();

  const topics = [
    "landingpage_archive_card_content_topic_architecture",
    "landingpage_archive_card_content_topic_film",
    "landingpage_archive_card_content_topic_science",
    "landingpage_archive_card_content_topic_music",
    "landingpage_archive_card_content_topic_food",
    "landingpage_archive_card_content_topic_history",
    "landingpage_archive_card_content_topic_design",
    "landingpage_archive_card_content_topic_technology",
    "landingpage_archive_card_content_topic_philosophy",
  ] as const;

  return (
    <ArchiveCard
      kickerKey="landingpage_archive_card_content_kicker"
      titleKey="landingpage_archive_card_content_title"
      bodyKey="landingpage_archive_card_content_body"
      span="col-span-1"
    >
      <div className="mt-6 flex flex-wrap gap-x-4 gap-y-1">
        {topics.map((key, i) => (
          <span key={key} className="text-xs text-neutral-500">
            {i > 0 && <span className="text-neutral-300">&middot;&nbsp;</span>}
            {translate(key)}
          </span>
        ))}
      </div>
    </ArchiveCard>
  );
}

function ArchiveCardVocabulary() {
  const { translate } = useTranslation();

  return (
    <ArchiveCard
      kickerKey="landingpage_archive_card_vocabulary_kicker"
      titleKey="landingpage_archive_card_vocabulary_title"
      bodyKey="landingpage_archive_card_vocabulary_body"
      span="col-span-1"
    >
      <div className="mt-6">
        <p className="text-sm leading-7 text-neutral-700 sm:text-base">
          {translate("landingpage_archive_card_vocabulary_sentence_prefix")}
          <span className="font-semibold text-neutral-900">
            {translate("landingpage_archive_card_vocabulary_highlight_word")}
          </span>
          {translate("landingpage_archive_card_vocabulary_sentence_suffix")}
        </p>
        <p className="mt-3 text-sm text-neutral-500">
          {translate("landingpage_archive_card_vocabulary_translation_hint")}
        </p>
      </div>
    </ArchiveCard>
  );
}

function ArchiveCardLexicon() {
  const { translate } = useTranslation();

  const artifacts = [
    {
      wordKey: "landingpage_archive_card_lexicon_word_1",
      translationKey: "landingpage_archive_card_lexicon_translation_1",
      contextKey: "landingpage_archive_card_lexicon_context_1",
    },
    {
      wordKey: "landingpage_archive_card_lexicon_word_2",
      translationKey: "landingpage_archive_card_lexicon_translation_2",
      contextKey: "landingpage_archive_card_lexicon_context_2",
    },
    {
      wordKey: "landingpage_archive_card_lexicon_word_3",
      translationKey: "landingpage_archive_card_lexicon_translation_3",
      contextKey: "landingpage_archive_card_lexicon_context_3",
    },
  ] as const;

  return (
    <ArchiveCard
      kickerKey="landingpage_archive_card_collection_kicker"
      titleKey="landingpage_archive_card_collection_title"
      bodyKey="landingpage_archive_card_collection_body"
      span="col-span-2"
    >
      <div className="mt-6 grid gap-6 sm:grid-cols-3">
        {artifacts.map((a) => (
          <div key={a.wordKey}>
            <p className="text-lg font-semibold text-neutral-900">
              {translate(a.wordKey)}
            </p>
            <p className="mt-1 text-sm text-neutral-500">
              {translate(a.translationKey)}
            </p>
            <p className="mt-2 text-xs leading-5 text-neutral-400">
              {translate(a.contextKey)}
            </p>
          </div>
        ))}
      </div>
    </ArchiveCard>
  );
}

function ArchiveCardCreation() {
  const { translate } = useTranslation();

  return (
    <ArchiveCard
      kickerKey="landingpage_archive_card_creation_kicker"
      titleKey="landingpage_archive_card_creation_title"
      bodyKey="landingpage_archive_card_creation_body"
      span="col-span-1"
    >
      <div className="mt-6">
        <p className="text-lg font-semibold leading-snug text-neutral-900">
          {translate("landingpage_archive_card_creation_sample_title")}
        </p>
        <p className="mt-1 text-xs text-neutral-400">
          {translate("landingpage_archive_card_creation_sample_meta")}
        </p>
        <p className="mt-3 text-sm leading-7 text-neutral-600">
          {translate("landingpage_archive_card_creation_sample_text")}
        </p>
      </div>
    </ArchiveCard>
  );
}

function ArchiveCardInsight() {
  return (
    <ArchiveCard
      kickerKey="landingpage_archive_card_insight_kicker"
      titleKey="landingpage_archive_card_insight_title"
      bodyKey="landingpage_archive_card_insight_body"
      span="col-span-2"
    >
    </ArchiveCard>
  );
}

function ArchiveCardClosing() {
  const { translate } = useTranslation();

  return (
    <article className="col-span-2 rounded-xl bg-gray-50 p-8 sm:p-12">
      <h2 className="text-2xl font-semibold leading-[1.1] tracking-[-0.03em] text-neutral-900 sm:text-3xl lg:text-4xl">
        {translate("landingpage_archive_closing_title")}
      </h2>
      <p className="mt-6 max-w-2xl text-base leading-7 text-neutral-600 sm:text-lg sm:leading-8">
        {translate("landingpage_archive_closing_body")}
      </p>
      <p className="mt-8 text-base font-medium text-neutral-900">
        {translate("landingpage_archive_closing_cta")} &rarr;
      </p>
    </article>
  );
}
